import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useNavigate } from "@/hooks/useDeskNavigate";
import useGoBack from "@/hooks/useGoBack";
import { toast } from "@/lib/toast";
import {
  ArrowLeft, Check, X, Play, CheckCircle2, Trash2, Repeat,
  ExternalLink, User, Clock, Calendar as CalendarIcon,
  StopCircle, Plus, MessageSquare, RotateCcw, Eye, Pencil,
  Bell, History, Download, FileText, FileSpreadsheet, LockIcon as LockIcon,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AvatarBlock } from "@/components/settings/AccountSection";
import AssigneesBlock from "@/components/tasks/AssigneesBlock";
import RecurrenceBlock from "@/components/tasks/RecurrenceBlock";
import StatusBadge from "@/components/tasks/StatusBadge";
import PriorityBadge from "@/components/tasks/PriorityBadge";
import ChecklistEditor from "@/components/tasks/ChecklistEditor";
import AttachmentsPanel from "@/components/tasks/AttachmentsPanel";
import Chat from "@/components/tasks/Chat";
import TaskEditDialog from "@/components/tasks/TaskEditDialog";
import DeskConfirm from "@/components/crm/DeskConfirm";
import ClosedTaskBanner from "@/components/tasks/ClosedTaskBanner";
import { hasRight } from "@/components/crm/crmShared";
import TaskHistoryDialog from "@/components/tasks/TaskHistoryDialog";
import { RelanceTile, RelanceDialog } from "@/components/tasks/RelanceDialog";
import { DeadlineInline } from "@/components/tasks/DeadlineDisplay";
import api from "@/lib/api";
import { markSeen } from "@/lib/seen";
import { toastRappelsReactivated } from "@/lib/rappelsToast";
import ReopenReasonModal from "@/components/shared/ReopenReasonModal";
import ConfirmDestructiveDialog from "@/components/common/ConfirmDestructiveDialog";

// Base URL for direct file downloads. `api.defaults.baseURL` already points
// to `${REACT_APP_BACKEND_URL}` (no trailing `/api`), so we can reuse it.
const API_BASE = api.defaults.baseURL || "";

/**
 * Download a protected file (CSV/PDF) via fetch so the bearer token is
 * attached — `<a href>` cannot carry Authorization headers. We then turn
 * the blob into an object URL and force a click on a hidden anchor to
 * trigger the browser's native "Save as…" behaviour.
 */
async function downloadWithAuth(path, filename) {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke shortly after so the download has time to start.
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (err) {
    // Errors surface through the shared axios interceptor already; just
    // log to console — no toast here to keep the dropdown lightweight.
    console.error("Download failed:", err);
  }
}
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { MODULE_COLORS } from "@/lib/moduleColors";
import HeaderProfileGroup from "@/components/TopBar";

const YELLOW = MODULE_COLORS.tasks;           // #F9BC26  · Tâches module
const ROLE_LABEL = { executor: "Exécuteur", manager: "Manager", helper: "Aide exécuteur" };
const FREQ_LABEL = { hourly: "Horaire", daily: "Quotidien", weekly: "Hebdo", monthly: "Mensuel", quarterly: "Trimestriel", semiannual: "Semestriel", yearly: "Annuel", custom: "Personnalisé" };

const fmtDT = (iso) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

/** Small square action button — icon + label underneath, module-coloured.
 *  `iconSize` (px): default 38 for secondary tiles, 44 for the primary
 *  "Terminée"/"Commencer" tile (harmonised sizes across the whole app). */
/**
 * ActionTile — square icon button (56×56) with a label underneath.
 *
 * All 4 tiles on the task detail action bar share the SAME size — the
 * primary/secondary distinction has been dropped. Sizing is locked in
 * index.css via the .action-tile class so no per-tile inline override can
 * cause visual drift.
 */
function ActionTile({ icon, label, color, onClick, badge, testId, disabled, title, done = false, dim = false }) {
  // iter410 L1-4 (S5 + K4) — case d'une barre segmentée : aucun fond de couleur, icône 22 px en couleur, libellé 11 px.
  // `done` : action déjà faite (garde ses couleurs, libellé en couleur weight 500) ; `dim` : autres cases en gris, opacité 0,4.
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || done}
      data-testid={testId}
      title={title}
      data-done={done || undefined}
      className="td-seg-cell"
      style={dim ? { opacity: 0.4, filter: "grayscale(1)" } : undefined}
    >
      <span className="td-seg-icon relative" style={{ color }}>
        {icon}
        {badge && (
          <span
            className="absolute -top-1.5 -right-2 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold"
            style={{ background: color, color: "#1B1B1B" }}
            aria-hidden="true"
          >
            {badge}
          </span>
        )}
      </span>
      <span className="td-seg-label" style={done ? { color, fontWeight: 500 } : undefined}>{label}</span>
    </button>
  );
}

export default function TaskDetail({ idOverride = null }) {
  // Spec iter307 — `idOverride` : montage en aperçu rapide (QuickViewDialog) hors route.
  const { id: routeId } = useParams();
  const id = idOverride || routeId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const canEditTask = can("taches", "edit");
  const canDeleteTask = can("taches", "delete");
  const [searchParams] = useSearchParams();
  // Deep-link support: mention notifications navigate to
  // `/taches/{id}?msg={messageId}` — Chat.jsx scrolls to & highlights it.
  const highlightMsgId = searchParams.get("msg") || null;
  // Row-level shortcut from TaskListView: `?scroll=chat` auto-scrolls the
  // Discussion section into view once the task has loaded. Spec 2026-02-16.
  const scrollTarget = searchParams.get("scroll") || null;

  // "Retour" preserves the navigation context: if we have a previous entry
  // in the browser history (came from Kanban / Calendrier / a filtered
  // list), pop it so the previous tab + scroll position + URL query
  // (?view=calendar&month=YYYY-MM) are restored. Otherwise fall back to a
  // clean list view.
  const goBack = useGoBack("/taches");
  const [task, setTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [relanceOpen, setRelanceOpen] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeReason, setCompleteReason] = useState("");
  const [redoOpen, setRedoOpen] = useState(false);
  const [redoReason, setRedoReason] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  // Spec 2026-02-XX rev46 (Point 2) — confirm dialog for creator's
  // shortcut close when other executors haven't clicked "Terminé".
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  // Spec iter212 (2026-02-28) — remplace `window.confirm` par ConfirmDestructiveDialog.
  const [stopRecurrenceOpen, setStopRecurrenceOpen] = useState(false);
  // Spec iter135 (Feb 2026) — modale de réouverture avec motif optionnel.
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);

  // Spec rev63 (2026-08-02) — deep-link deputy support for Edit / Delete
  // row shortcuts (`TaskListView.jsx` actions column). Cliquer sur les
  // icônes ✎ Modifier / 🗑 Supprimer d'une ligne du tableau ouvre
  // désormais AUTOMATIQUEMENT le dialogue correspondant après le
  // chargement de la tâche (via `?edit=1` ou `?delete=1`), au lieu de
  // n'avoir aucun effet visible.
  const [editParamConsumed, setEditParamConsumed] = useState(false);
  const [deleteParamConsumed, setDeleteParamConsumed] = useState(false);
  useEffect(() => {
    if (!task) return;
    if (searchParams.get("edit") === "1" && !editParamConsumed && canEditTask) {
      setEditOpen(true);
      setEditParamConsumed(true);
    }
    if (searchParams.get("delete") === "1" && !deleteParamConsumed && canDeleteTask) {
      setDeleteOpen(true);
      setDeleteParamConsumed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  const load = async () => {
    try {
      const [{ data: t }, { data: us }, { data: ps }] = await Promise.all([
        api.get(`/tasks/${id}`),
        api.get("/users/directory"),
        api.get("/projects"),
      ]);
      setTask(t); setUsers(us); setProjects(ps);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Accès refusé à cette tâche");
      else if (err.response?.status === 404) toast.error("Tâche introuvable");
      navigate("/taches", { replace: true });
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  // iter372 pt 1 — ouverte → plus comptée dans la barre latérale (à l'ouverture et en quittant).
  useEffect(() => { markSeen("tasks", id); return () => { markSeen("tasks", id); }; }, [id]);

  // Spec 2026-07-24 feature #9 — badge de lecture. Marquer la tâche
  // comme "lue" par l'assigné actuel dès qu'il ouvre la fiche.
  //
  // Spec 2026-02-XX rev5 (Feb 2026) — bug corrigé : la garde ne filtrait
  // que ``role === "executor"``, ce qui empêchait les helper/manager
  // (ex: Fawzi) de faire enregistrer leur ``first_read_at``. On tolère
  // désormais N'IMPORTE QUEL rôle d'assignation (le backend accepte aussi).
  useEffect(() => {
    if (!task?.id || !user?.id) return;
    const myAssignment = (task.assignments || []).find(
      (a) => a.user_id === user.id,
    );
    if (myAssignment && !myAssignment.first_read_at) {
      api.post(`/tasks/${task.id}/mark-read`)
        .then(({ data }) => {
          if (data?.updated) load();
        })
        .catch(() => { /* silencieux — pas critique */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, user?.id]);

  // Spec 2026-02-16 — when the URL carries `?scroll=chat` (opened via the
  // dedicated Discussion cell on the Tasks list), auto-scroll the chat
  // section into view once the task has hydrated. Delays a beat so the
  // Chat renders its bubbles before we scroll.
  useEffect(() => {
    if (!task || scrollTarget !== "chat") return;
    const t = setTimeout(() => {
      const el = document.getElementById("chat");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [task, scrollTarget]);

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const project = useMemo(() => projects.find((p) => p.id === task?.project_id), [projects, task]);

  if (!task) {
    return <div data-testid="task-detail-page" className="text-muted-foreground">Chargement…</div>;
  }

  const myAssignment = (task.assignments || []).find((a) => a.user_id === user?.id);
  const isAdmin = user?.role === "admin";
  const isCreator = task.created_by === user?.id;

  // ------- Actions ---------------------------------------------------------
  const decide = async (accept) => {
    if (!accept && !refuseReason.trim()) return;
    try {
      const { data } = await api.post(`/tasks/${task.id}/decision`, { accept, reason: refuseReason.trim() });
      setTask(data);
      setRefuseOpen(false); setRefuseReason("");
      toast.success(accept ? "Tâche acceptée" : "Tâche refusée");
    } catch (err) {
      toast.error("Action refusée", { description: err.response?.data?.detail || "" });
    }
  };
  // Spec iter135 (Feb 2026) — réouverture avec motif optionnel. Ouverte
  // via le bouton « Rouvrir » (état closed) qui déclenche la modale
  // ``ReopenReasonModal``. Le motif est envoyé au backend s'il est
  // renseigné, sinon la réouverture est effectuée sans motif (comme
  // avant iter135).
  const reopenTaskWithReason = async (reason) => {
    if (reopenBusy) return;
    setReopenBusy(true);
    try {
      const payload = reason ? { reason } : {};
      const { data } = await api.post(`/tasks/${task.id}/reopen`, payload);
      setTask(data);
      toast.success("Tâche rouverte");
      toastRappelsReactivated();
      setReopenModalOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réouverture impossible");
    } finally {
      setReopenBusy(false);
    }
  };
  const startWork = async () => {
    try { const { data } = await api.post(`/tasks/${task.id}/start`); setTask(data); toast.success("Tâche en cours"); }
    catch (err) { toast.error("Impossible", { description: err.response?.data?.detail || "" }); }
  };
  const completeWork = async () => {
    try {
      const { data } = await api.post(`/tasks/${task.id}/complete`, { reason: completeReason.trim() });
      setTask(data);
      setCompleteOpen(false); setCompleteReason("");
      if (data.status === "to_verify") {
        toast.success("Marquée terminée", { description: "En attente de vérification du créateur" });
      } else {
        toast.success("Tâche terminée");
      }
    }
    catch (err) { toast.error("Impossible", { description: err.response?.data?.detail || "" }); }
  };
  const redoTask = async () => {
    if (!redoReason.trim()) return;
    try {
      const { data } = await api.post(`/tasks/${task.id}/redo`, { reason: redoReason.trim() });
      setTask(data);
      setRedoOpen(false); setRedoReason("");
      toast.success("Tâche renvoyée", { description: "Les exécuteurs ont été notifiés" });
    }
    catch (err) { toast.error("Impossible", { description: err.response?.data?.detail || "" }); }
  };
  const closeTask = async () => {
    try {
      const { data } = await api.post(`/tasks/${task.id}/close`, {});
      setTask(data);
      toast.success("Tâche clôturée");
    }
    catch (err) { toast.error("Impossible", { description: err.response?.data?.detail || "" }); }
  };
  // Spec 2026-02-XX rev46 (Point 2) — Confirmation légère avant clôture
  // directe par le créateur si des exécuteurs autres que lui n'ont pas
  // encore cliqué « Terminé ». Évite un clic accidentel qui court-
  // circuite le travail en cours d'un collaborateur.
  const pendingExecutors = ((task.assignments || []).filter(
    (a) => a.role === "executor"
      && a.user_id !== task.created_by
      && a.accept_status !== "refused"
      && !a.completed_at,
  ));
  const requestClose = () => {
    if (pendingExecutors.length > 0 && task.status !== "to_verify") {
      setCloseConfirmOpen(true);
      return;
    }
    closeTask();
  };
  const stopRecurrence = async () => {
    // Spec iter212 — ouverture du dialogue de confirmation stylé.
    setStopRecurrenceOpen(true);
  };
  const performStopRecurrence = async () => {
    try { const { data } = await api.post(`/tasks/${task.id}/recurrence/stop`); setTask(data); toast.success("Récurrence arrêtée"); setStopRecurrenceOpen(false); }
    catch (err) { toast.error("Impossible", { description: err.response?.data?.detail || "" }); }
  };
  const removeTask = async (r) => {
    const reason = (r || deleteReason).trim();
    if (!reason) return;
    try {
      await api.delete(`/tasks/${task.id}`, { data: { reason } });
      toast.deleted("tache", task.reference || task.title || "", { onUndo: () => api.post(`/tasks/${task.id}/restore`) });
      navigate("/taches", { replace: true });
    } catch (err) { toast.error("Impossible", { description: err.response?.data?.detail || "" }); }
  };

  // Visibility of the primary Start/Complete tile — spec 2026-02-17.
  //   • Assignee whose task is `waiting` or `redo` → auto-flips to
  //     `in_progress` on first read (server-side). Then they see
  //     "Terminé" to submit for verification.
  //   • Creator / admin (not necessarily assigned) → "Terminé" as long
  //     as the task is not already `completed`/`closed`. Same target
  //     status `to_verify` — the creator will then Clôturer / Refaire.
  //   • Once task is in `to_verify`, the creator sees
  //     "Clôturer" + "À refaire" tiles instead.
  const isTerminal = ["completed", "closed"].includes(task.effective_status);
  // Tâche clôturée = figée : aucune action sauf « Rouvrir » (droit tasks.reopen).
  const frozen = task.status === "closed";
  const canReopen = frozen && (isAdmin || hasRight(user, "tasks.reopen"));
  const closerUser = usersById[task.closed_by || task.completed_by];
  const closedByName = task.closed_by_name || (closerUser ? `${closerUser.first_name || ""} ${closerUser.last_name || ""}`.trim() : "—");
  const closedBanner = <ClosedTaskBanner closedAt={task.closed_at || task.completed_at} closedBy={closedByName} canReopen={canReopen} onReopen={() => setReopenModalOpen(true)} />;
  const isToVerify = task.status === "to_verify" || task.effective_status === "to_verify";
  const isCreatorOrAdmin = isCreator || isAdmin;
  const iAlreadyCompleted = !!(myAssignment && myAssignment.completed_at);

  // Spec 2026-08-02 point #1 — droit de clôture réservé au créateur.
  // Fallback admin autorisé UNIQUEMENT si le créateur est désactivé
  // (compte inactif OU supprimé OU introuvable dans la liste users).
  const creatorUser = usersById[task.created_by];
  const creatorAvailable = !!(
    creatorUser
    && creatorUser.status === "active"
    && !creatorUser.is_deleted
  );
  // Iter124 — un rôle Utilisateur ne clôture jamais (il marque « Terminée » → « À vérifier »).
  // Admin : sa propre tâche, ou une tâche créée par un Utilisateur, ou créateur admin indisponible.
  const creatorIsAdmin = creatorUser?.role === "admin";
  const hasCloseRight = can("tasks", "close");
  // iter426 pt 2 — un exécutant ne clôture jamais sa propre tâche, quel que soit son rôle.
  const isExecutorHere = !!myAssignment && (myAssignment.role || "executor") === "executor" && !isCreator;
  const canClose = hasCloseRight && !isExecutorHere && (isCreator || !creatorIsAdmin || !creatorAvailable);
  const closeDisabledReason = !canClose
    ? (isExecutorHere ? "Un exécutant ne clôture pas sa propre tâche : seul le créateur clôture" : hasCloseRight ? "Seul le créateur peut clôturer cette tâche" : "Droit de clôture requis")
    : null;

  // Spec 2026-02-17 — accept/refuse cycle removed. No more pending gate.
  const showDecisionBanner = false;
  const isPendingGate = false;

  const canStart = false; // Auto-transition on read replaces manual /start
  const canComplete =
    !isTerminal &&
    !isToVerify &&
    !iAlreadyCompleted &&
    (!!myAssignment || isCreator || isAdmin);
  const canVerify = isToVerify && isAdmin;
  // iter402 pt 1a — Relancer : créateur / vérificateurs (admins) / Premier Admin, jamais l'assigné lui-même, masqué si Terminé ou Clôturée.
  // iter424 pt 4 — Relancer masqué dès « À vérifier » (l'exécutant a terminé), Terminée ou Clôturée ; « Refaire » le rend à nouveau possible.
  const canRelance = !isTerminal && !isToVerify && !["completed", "closed", "to_verify"].includes(task.status) && !myAssignment && (isCreator || isAdmin || !!user?.is_first_admin) && (task.assignments || []).length > 0;
  const relanceTile = canRelance ? <RelanceTile count={task.relances?.count || 0} onClick={() => setRelanceOpen(true)} /> : null;
  const canRecurrence = task.recurrence?.active && (isAdmin || isCreator);

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="task-detail-page">
      {/* Back link + Edit shortcut */}
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={goBack} data-testid="td-back">
          <ArrowLeft size={16} className="mr-1" /> Retour
        </Button>
        <div className="flex items-center gap-2">
          {(isCreator || isAdmin) && !isTerminal && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
              className="gap-1.5 border-vertec-blue text-vertec-blue hover:bg-vertec-blue/10"
              data-testid="td-edit"
              disabled={!canEditTask}
              title={canEditTask ? undefined : "Permission requise"}
            >
              <Pencil size={14} /> Modifier
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHistoryOpen(true)}
            className="gap-1.5 border-[#666] text-foreground hover:bg-white/5"
            data-testid="td-history-btn"
            title="Consulter l'historique de la tâche"
          >
            <History size={14} /> Historique
          </Button>
          {/*
            Full task PDF export (spec 2026-02-18 sexies point 3).
            Kept as its OWN button (distinct from the history "Exporter"
            dropdown) so users can grab a complete detail sheet in one
            click without navigating the CSV/PDF sub-menu. Same download
            path via `downloadWithAuth` to attach the bearer token.
          */}
          {/* iter410 L1-1 : « Fiche PDF » et « Exporter » (historique) supprimés — routes d'export désactivées côté serveur */}
          <div className="shrink-0" style={{ width: 1, height: 26, background: "#333", marginLeft: 4 }} aria-hidden="true" />
          <HeaderProfileGroup />
        </div>
      </div>

      {/* ==== Title + status badges ============================================ */}
      <div className="space-y-2">
        {/* Reference badge + created-at — always visible for traceability */}
        <div className="flex items-center gap-3 text-xs">
          {task.reference && (
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-md font-mono font-semibold tracking-wider text-[#1B1B1B]"
              style={{ background: YELLOW }}
              data-testid="td-reference"
            >
              {task.reference}
            </span>
          )}
          <span className="text-muted-foreground" data-testid="td-created-at">
            Créée le {fmtDT(task.created_at)}
          </span>
        </div>
        <h1 className="text-3xl font-bold" data-testid="td-subject">{task.subject}{task.indice && <><span className="font-normal" style={{ color: "#a4a4ae" }}> : </span><span style={{ color: "#f4c877", fontWeight: 500 }} data-testid="td-subject-indice">{task.indice}</span></>}</h1>
        <div className="flex flex-wrap gap-2">
          <StatusBadge status={task.effective_status} size="lg" />
          <PriorityBadge priority={task.priority} size="lg" />
          {task.recurrence?.active && (
            <Badge variant="outline" className="gap-1">
              <Repeat size={12} /> {FREQ_LABEL[task.recurrence.frequency]}
            </Badge>
          )}
        </div>
        {task.auto_from_type && (
          <div className="flex items-center flex-wrap" style={{ gap: 6, marginTop: 8, fontSize: 11, color: "#a4a4ae" }} data-testid="td-auto-task-subtitle">
            <span className="inline-flex items-center" style={{ gap: 4, fontSize: 9, textTransform: "uppercase", letterSpacing: ".5px", padding: "2px 7px", borderRadius: 4, background: "rgba(217,155,30,.14)", border: "0.5px solid rgba(217,155,30,.4)", color: "#e6c383" }}><CheckCircle2 size={10} />Tâche automatique</span>
            <span>· liée au document</span>
            <button type="button" onClick={() => navigate(`/documents/${task.linked_document_id}`)} className="inline-flex items-center hover:underline" style={{ gap: 4, color: "#7fa8dd", fontWeight: 500 }} data-testid="td-auto-task-doc-link">{task.linked_document_ref || task.linked_document_id}<ExternalLink size={11} /></button>
            {task.auto_task_source?.creator_name && <span style={{ color: "#71717c" }}>· Créée par {task.auto_task_source.creator_name} à partir du document {task.auto_task_source.reference || task.linked_document_ref}</span>}
          </div>
        )}
      </div>

      {/* ==== Actions bar =====================================================
          Layout swaps entirely while the task is still pending:
          - `showDecisionBanner` → we render a *single full-width banner*
            (per the redesign spec) instead of the tiles row.
          - Otherwise → the usual tiles row, still greyed-out via
            `isPendingGate` for any non-admin viewer while the executor
            hasn't accepted. */}
      {/* iter410 L1-4 : la barre reste visible sur une tâche terminée/clôturée (case faite en couleur, autres grisées) */}
      {(!frozen || isTerminal) && (
      <Card
        className="p-4 border-[#3A3A3A] shadow-[0_2px_8px_rgba(0,0,0,0.3)] space-y-4"
        style={{ background: "linear-gradient(135deg, #212121 0%, #1A1A1A 100%)" }}
      >
        {showDecisionBanner ? (
          <div
            className="flex flex-wrap items-center justify-between gap-4"
            style={{
              background: "#242424",
              border: "1.5px solid #3A3A3A",
              borderRadius: 14,
              padding: "20px 24px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
            data-testid="td-decision-banner"
          >
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: "#F9BC26",
                  boxShadow: "0 0 8px #F9BC26",
                }}
              />
              <span style={{ color: "#E5E5E5", fontSize: 14 }}>
                {myAssignment?.accept_status === "pending"
                  ? "Cette tâche vous a été assignée. Que souhaitez-vous faire ?"
                  : "Un ou plusieurs assignés n'ont pas encore répondu. Vous pouvez décider en leur nom."}
              </span>
            </div>
            <div className="flex items-center gap-[10px]">
              <button
                type="button"
                onClick={() => decide(true)}
                data-testid="td-accept"
                style={{
                  background: "#22C55E",
                  color: "#FFFFFF",
                  borderRadius: 999,
                  padding: "10px 22px",
                  fontWeight: 700,
                  border: "none",
                  transition: "filter .15s",
                }}
                className="hover:brightness-110 inline-flex items-center gap-1.5"
              >
                <Check size={14} strokeWidth={3} /> Accepter
              </button>
              <button
                type="button"
                onClick={() => setRefuseOpen(true)}
                data-testid="td-refuse"
                style={{
                  background: "transparent",
                  color: "#EF4444",
                  borderRadius: 999,
                  padding: "10px 22px",
                  fontWeight: 700,
                  border: "1.5px solid #EF4444",
                  transition: "background-color .15s",
                }}
                className="hover:bg-red-500/10 inline-flex items-center gap-1.5"
              >
                <X size={14} strokeWidth={3} /> Refuser
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center pt-2"><div className="td-seg-bar" data-testid="td-action-bar">
          {isTerminal && (
            <ActionTile
              icon={task.effective_status === "closed" ? <LockIcon strokeWidth={2.4} /> : <CheckCircle2 strokeWidth={2.4} />}
              label={task.effective_status === "closed" ? "Clôturer" : "Terminé"}
              color={task.effective_status === "closed" ? "#A78BFA" : "#22C55E"}
              done
              testId="td-done-state"
            />
          )}
          {canVerify ? (
            <>
              <ActionTile
                icon={<RotateCcw strokeWidth={2.4} />}
                label="Refaire"
                color="#FF8C00"
                bgColor="rgba(255,140,0,0.15)"
                onClick={() => setRedoOpen(true)}
                testId="td-redo"
              />
              {/* Spec iter115bis (Feb 2026) — Clôturer en outline pour ne
                  plus paraître "déjà pressé". Aligné sur le shortcut créateur
                  ci-dessous (rgba(139,92,246,0.15)) et cohérent avec
                  Supprimer/Récurrence qui utilisent aussi bg translucide. */}
              <ActionTile
                icon={<LockIcon strokeWidth={2.4} />}
                label="Clôturer"
                color="#A78BFA"
                bgColor="rgba(139,92,246,0.15)"
                disabled={!canClose}
                title={closeDisabledReason || undefined}
                onClick={canClose ? closeTask : undefined}
                testId="td-close"
              />
              {relanceTile}
              <span className="td-seg-divider" aria-hidden="true" />
            </>
          ) : (canStart || canComplete) && (
            <>
              <ActionTile
                icon={canStart && !canComplete
                  ? <Play strokeWidth={2.4} />
                  : <CheckCircle2 strokeWidth={2.4} />}
                label={canStart && !canComplete ? "Commencer" : "Terminé"}
                color="#22C55E"
                bgColor="#22C55E"
                filled
                onClick={canStart && !canComplete ? startWork : () => setCompleteOpen(true)}
                testId={canStart && !canComplete ? "td-start" : "td-complete"}
              />
              {/* Spec 2026-02-XX rev45 (Point 3+4) — Le créateur peut aussi
                  cliquer DIRECTEMENT sur « Clôturer » sans passer par
                  « Terminé » d'abord. Utile quand il a réalisé le travail
                  lui-même sans passer par un exécuteur. La règle métier
                  reste stricte : seul le créateur voit ce raccourci. */}
              {canClose && !iAlreadyCompleted && (
                <ActionTile
                  icon={<LockIcon strokeWidth={2.4} />}
                  label="Clôturer"
                  color="#A78BFA"
                  bgColor="rgba(139,92,246,0.15)"
                  onClick={requestClose}
                  title="Clôturer directement (raccourci créateur)"
                  testId="td-close-shortcut"
                />
              )}
              {relanceTile}
              <span className="td-seg-divider" aria-hidden="true" />
            </>
          )}
          {iAlreadyCompleted && !isTerminal && !canVerify && (
            <>
              <ActionTile
                icon={<Eye strokeWidth={2.4} />}
                label="En attente"
                color="#8B5CF6"
                bgColor="rgba(139,92,246,0.12)"
                disabled
                testId="td-waiting-verify"
              />
              <span className="td-seg-divider" aria-hidden="true" />
            </>
          )}
          <span className="td-seg-divider td-seg-divider--thick" aria-hidden="true" />
          {canRecurrence ? (
            <ActionTile
              icon={<StopCircle strokeWidth={2} />}
              label="Arrêter récurrence"
              color="#3B9EFF"
              dim={isTerminal}
              onClick={stopRecurrence}
              testId="td-stop-recurrence"
            />
          ) : (
            <ActionTile
              icon={<Repeat strokeWidth={2} />}
              label="Récurrence"
              color="#3B9EFF"
              disabled
              dim={isTerminal}
              testId="td-recurrence-disabled"
            />
          )}
          <ActionTile
            icon={<Trash2 strokeWidth={2} />}
            label="Supprimer"
            color="#EF4444"
            dim={isTerminal}
            onClick={() => setDeleteOpen(true)}
            disabled={!(isCreator || !!user?.is_first_admin) || !canDeleteTask}
            title={!(isCreator || !!user?.is_first_admin) ? "Seul le créateur de la tâche peut la supprimer" : (!canDeleteTask ? "Permission requise" : undefined)}
            testId="td-delete"
          />
        </div></div>
        )}
      </Card>
      )}

      {/* ==== Closure info card (visible only when the task is done) ========== */}
      {task.completed_at && (
        <Card
          className="p-4 border-[#3A3A3A] shadow-[0_2px_8px_rgba(0,0,0,0.3)] flex items-start gap-3"
          style={{
            borderLeft: `4px solid #22C55E`,
            background: "linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.02) 100%)",
          }}
          data-testid="td-closure-card"
        >
          <CheckCircle2 size={18} style={{ color: "#22C55E" }} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "#22C55E" }}>
              Clôturée le {fmtDT(task.completed_at)}
            </div>
            {task.completion_reason && (
              <p className="text-sm mt-1.5 whitespace-pre-wrap" data-testid="td-completion-reason">
                {task.completion_reason}
              </p>
            )}
          </div>
        </Card>
      )}

      {/* ==== Rouvrir — spec iter119 pt.3 (Feb 2026) ============================
          Visible UNIQUEMENT quand la tâche est en statut ``closed``. Réservé
          Admin / Premier Admin. Une pilule au-dessus indique qui a clôturé et
          quand, avec l'avatar de la personne. Au clic sur « Rouvrir » : le
          statut repasse à ``in_progress`` (sans motif requis, spec). */}
      {frozen && closedBanner}

      {/* ==== Infos (yellow left border = Tâches module accent) ================ */}
      <Card
        className="p-6 border-[#3A3A3A] shadow-[0_2px_8px_rgba(0,0,0,0.3)]"
        style={{ borderLeft: `4px solid ${YELLOW}` }}
        data-testid="td-info-card"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          {project && (
            <div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-widest mb-1">Siège / Projet</div>
              <div className="font-medium">{project.name}</div>
            </div>
          )}
          <div>
            <div className="text-[11px] text-muted-foreground uppercase tracking-widest mb-1 flex items-center gap-1">
              <CalendarIcon size={11} /> Exécution
            </div>
            <div className="font-medium">{fmtDT(task.execution_date)}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground uppercase tracking-widest mb-1 flex items-center gap-1">
              <Clock size={11} /> Deadline
            </div>
            {/* Spec rev152 (2026-02-07) — palette rev150 alignée sur Liste/Kanban. */}
            <div data-testid="td-deadline">
              <DeadlineInline iso={task.deadline} size={26} doneAt={task.done_at} />
            </div>
          </div>
        </div>

        {task.doc_link && (
          <a href={task.doc_link} target="_blank" rel="noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-vertec-blue hover:underline"
            data-testid="td-doclink">
            <ExternalLink size={14} /> Document lié
          </a>
        )}
      </Card>

      {task.recurrence && <RecurrenceBlock recurrence={task.recurrence} indice={task.indice} />}
      {!task.recurrence && task.indice && (
        <div className="flex items-center gap-2 text-xs" style={{ color: "#a4a4ae" }} data-testid="td-indice-line"><span className="uppercase tracking-wide text-[10px]" style={{ color: "#71717c" }}>Indice</span><span className="text-white" data-testid="td-indice">{task.indice}</span></div>
      )}

      {/* ==== Reminders programmed (yellow left border) ======================== */}
      <RemindersCard
        task={task}
        onDeleted={(remId) =>
          setTask((prev) => prev ? { ...prev, reminders: (prev.reminders || []).filter((r) => r.id !== remId) } : prev)
        }
      />

      {/* ==== Description — highlight card (yellow filet + dark yellow bg) ==== */}
      {task.message && (
        <Card
          className="p-5 border-[#3A3A3A] shadow-[0_2px_8px_rgba(0,0,0,0.3)]"
          style={{
            borderLeft: `4px solid ${YELLOW}`,
            background: "linear-gradient(135deg, rgba(249,188,38,0.06) 0%, rgba(249,188,38,0.02) 100%)",
          }}
          data-testid="td-message-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare size={14} style={{ color: YELLOW }} />
            <span className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: YELLOW }}>
              Description
            </span>
          </div>
          <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="td-message">{task.message}</p>
        </Card>
      )}

      {/* ==== Assignés — cartes par personne (Point 4, Sept 2026) ============== */}
      <Card className="p-5 border-[#3A3A3A] shadow-[0_2px_8px_rgba(0,0,0,0.3)]" style={{ borderLeft: `4px solid ${YELLOW}` }}>
        <AssigneesBlock assignments={task.assignments || []} usersById={usersById} serverNow={task.server_now} />
      </Card>

      {/* ==== Checklist + Attachments — 2 columns ============================== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChecklistEditor task={task} onChanged={setTask} frozen={frozen} />
        <AttachmentsPanel taskId={task.id} frozen={frozen} />
      </div>

      {/* ==== Chat =============================================================
           Spec 2026-02-18 point 5 — hidden entirely when the task is solo
           (creator is the only assignee, no one else to discuss with). */}
      {(() => {
        const isSolo = (task.assignments || []).every((a) => a.user_id === task.created_by);
        const isAdminLevel = isAdmin || !!user?.is_first_admin;
        if (isSolo && !isAdminLevel) {
          return (
            <div className="rounded-lg border border-dashed border-vertec-borderDark p-6 text-center" data-testid="td-chat-solo">
              <MessageSquare size={22} className="mx-auto mb-2 opacity-40" style={{ color: "#6B7280" }} />
              <p className="text-sm text-muted-foreground">
                <strong>Tâche solo</strong> — aucun autre assigné, pas de discussion possible.
              </p>
              <p className="text-xs text-muted-foreground mt-1.5 opacity-70">
                Ajoutez au moins un autre membre de l'équipe pour activer la discussion.
              </p>
            </div>
          );
        }
        return (
          <div data-testid="td-chat-section" id="chat">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2" data-testid="td-chat-title">
              <MessageSquare size={14} style={{ color: "#F5C518" }} /> Discussion &amp; relance
              <span className="inline-flex items-center gap-1 normal-case tracking-normal tabular-nums" style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "#2E2814", color: "#F5D96B" }} data-testid="td-chat-count-messages"><MessageSquare size={11} strokeWidth={2.4} /> {chatMsgs.filter((m) => (m.kind || "user") === "user" && m.msg_type !== "relance").length}</span>
              <span className="inline-flex items-center gap-1 normal-case tracking-normal tabular-nums" style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "#33202A", color: "#F0A0C4" }} data-testid="td-chat-count-relances"><Bell size={11} strokeWidth={2.4} /> {task.relances?.count || 0}</span>
            </h3>
            <Chat
              task={task}
              users={users}
              currentTaskAssignments={task.assignments || []}
              highlightMessageId={highlightMsgId}
              frozen={frozen}
              onMessagesChange={setChatMsgs}
            />
          </div>
        );
      })()}

      {/* ---- Refuse dialog --------------------------------------------------- */}
      <Dialog open={refuseOpen} onOpenChange={setRefuseOpen}>
        <DialogContent className="bg-card border-[#3A3A3A]" data-testid="refuse-dialog">
          <DialogHeader><DialogTitle>Motif du refus</DialogTitle></DialogHeader>
          <Textarea
            value={refuseReason}
            onChange={(e) => setRefuseReason(e.target.value)}
            placeholder="Expliquez pourquoi vous refusez cette tâche… (obligatoire)"
            rows={4}
            data-testid="refuse-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefuseOpen(false)}>Annuler</Button>
            <Button
              onClick={() => decide(false)}
              disabled={!refuseReason.trim()}
              className="bg-status-danger hover:bg-status-danger/80 text-white"
              data-testid="refuse-confirm"
            >
              Confirmer le refus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Delete confirm dialog (motif obligatoire) --------------------- */}
      <RelanceDialog open={relanceOpen} task={task} usersById={usersById} onClose={() => setRelanceOpen(false)} onDone={() => load()} />
      <DeskConfirm open={deleteOpen} danger withReason title="Mettre la tâche à la corbeille ?" message="La tâche sera définitivement supprimée 30 jours après cette action." confirmLabel="Supprimer" reasonPlaceholder="Motif de suppression (obligatoire)" onClose={() => { setDeleteOpen(false); setDeleteReason(""); }} onConfirm={(r) => { setDeleteReason(r); removeTask(r); }} testId="delete" />

      {/* ---- Complete confirm dialog (with optional reason) ---------------- */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="bg-card border-[#3A3A3A]" data-testid="complete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 size={18} style={{ color: "#22C55E" }} />
              Marquer comme terminée
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Ajoutez un commentaire pour indiquer <em>comment</em> ou <em>pourquoi</em> cette
            tâche est close. Ce motif reste consultable sur la fiche.
          </p>
          <div className="form-section p-0">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1 block">
              Motif / commentaire <span className="normal-case tracking-normal opacity-70">(optionnel)</span>
            </label>
            <Textarea
              value={completeReason}
              onChange={(e) => setCompleteReason(e.target.value)}
              placeholder="Ex : Documents validés et transmis au cabinet."
              rows={3}
              data-testid="complete-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Annuler</Button>
            <Button
              onClick={completeWork}
              className="text-white gap-1.5 border-0"
              style={{ background: "#22C55E", boxShadow: "0 4px 14px rgba(34,197,94,0.3)" }}
              data-testid="complete-confirm"
            >
              <CheckCircle2 size={14} strokeWidth={2.5} /> Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Confirmation shortcut close (rev46 Point 2) ---
           Modale légère si le créateur clôture alors qu'il reste des
           exécuteurs qui n'ont pas cliqué "Terminé". */}
      <Dialog open={closeConfirmOpen} onOpenChange={setCloseConfirmOpen}>
        <DialogContent className="bg-card border-[#3A3A3A]" data-testid="close-shortcut-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 size={18} style={{ color: "#22C55E" }} />
              Clôturer sans passer par la vérification ?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              {pendingExecutors.length === 1
                ? "Un exécuteur n'a pas encore cliqué « Terminé »."
                : `${pendingExecutors.length} exécuteurs n'ont pas encore cliqué « Terminé ».`}
              {" "}En clôturant maintenant, vous court-circuitez leur signalement de fin de travail.
            </p>
            <ul
              className="rounded-md p-2 text-[12px] space-y-0.5"
              style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.35)", color: "#F87171" }}
            >
              {pendingExecutors.map((a) => {
                const u = usersById[a.user_id];
                const name = u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : a.user_id.slice(0, 8);
                return <li key={a.id}>· {name}</li>;
              })}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseConfirmOpen(false)} data-testid="close-shortcut-cancel">
              Annuler
            </Button>
            <Button
              onClick={() => { setCloseConfirmOpen(false); closeTask(); }}
              className="text-white gap-1.5 border-0"
              style={{ background: "var(--status-cloture-tasks)", boxShadow: "0 4px 14px rgba(139,92,246,0.35)" }}
              data-testid="close-shortcut-confirm-btn"
            >
              <LockIcon size={14} strokeWidth={2.5} /> Clôturer quand même
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Redo dialog (creator sends the task back with mandatory motif) --- */}
      <Dialog open={redoOpen} onOpenChange={setRedoOpen}>
        <DialogContent className="bg-card border-[#3A3A3A]" data-testid="redo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw size={18} style={{ color: "#FF8C00" }} />
              Refaire la tâche
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Renvoyez la tâche au(x) exécuteur(s) avec un motif expliquant
            pourquoi elle n&apos;est pas considérée terminée. Le motif sera
            enregistré dans la discussion et notifié par e-mail.
          </p>
          <div className="form-section p-0">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1 block">
              Pourquoi ce n&apos;est pas terminé ?{" "}
              <span className="normal-case tracking-normal opacity-70">(obligatoire)</span>
            </label>
            <Textarea
              value={redoReason}
              onChange={(e) => setRedoReason(e.target.value)}
              placeholder="Ex : Le tableau n'a pas été mis à jour."
              rows={3}
              data-testid="redo-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedoOpen(false)}>Annuler</Button>
            <Button
              onClick={redoTask}
              disabled={!redoReason.trim()}
              className="text-white gap-1.5 border-0 disabled:opacity-50"
              style={{ background: "#FF8C00", boxShadow: "0 4px 14px rgba(255,140,0,0.3)" }}
              data-testid="redo-confirm"
            >
              <RotateCcw size={14} strokeWidth={2.5} /> Renvoyer avec motif
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Edit dialog (creator / admin) ------------------------------- */}
      <TaskEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        task={task}
        users={users}
        projects={(projects || []).filter((p) => p.in_choices !== false)}
        onUpdated={(fresh) => setTask(fresh)}
      />

      {/* ---- History dialog (audit log, visible to anyone who can see the task) --- */}
      <TaskHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        taskId={task.id}
      />

      {/* Spec iter135 (Feb 2026) — modale de réouverture avec motif optionnel. */}
      <ReopenReasonModal
        open={reopenModalOpen}
        busy={reopenBusy}
        entityLabel="cette tâche"
        onClose={() => { if (!reopenBusy) setReopenModalOpen(false); }}
        onConfirm={reopenTaskWithReason}
      />
      {/* Spec iter212 — Confirmation stylée pour arrêt de récurrence. */}
      <ConfirmDestructiveDialog
        open={stopRecurrenceOpen}
        onOpenChange={setStopRecurrenceOpen}
        title="Arrêter la récurrence ?"
        description="L'historique reste conservé — seules les prochaines occurrences ne seront plus générées."
        icon={<Repeat size={20} />}
        confirmLabel="Arrêter"
        busyLabel="Arrêt…"
        onConfirm={performStopRecurrence}
        testId="confirm-stop-recurrence"
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// RemindersCard — displays the list of custom reminders (before/during/after)
// with a per-row 🗑 delete button. Hidden entirely when no reminder exists.
// Spec 2026-02-15 (Lot A · point 1).
// ---------------------------------------------------------------------------
const REMINDER_STYLE = {
  before: { label: "Avant",   color: "#0078D4", bg: "rgba(0,120,212,0.15)" },
  during: { label: "Pendant", color: "#F97316", bg: "rgba(249,115,22,0.15)" },
  after:  { label: "Après",   color: "#EF4444", bg: "rgba(239,68,68,0.15)" },
};

function humanDuration(minutes) {
  if (minutes == null || Number.isNaN(minutes)) return "";
  const abs = Math.abs(Math.round(minutes));
  if (abs === 0) return "à l'instant";
  if (abs < 60)   return `${abs} min`;
  if (abs < 24 * 60) {
    const h = Math.round(abs / 60);
    return `${h} h`;
  }
  const d = Math.round(abs / (24 * 60));
  return `${d} j`;
}

function describeReminder(rem, anchorIso) {
  const anchor = anchorIso ? new Date(anchorIso).getTime() : null;
  const at = rem.at ? new Date(rem.at).getTime() : null;
  const interval = rem.interval_minutes;
  if (rem.type === "after" && interval) {
    // Repeated reminder after the deadline
    return `Chaque ${humanDuration(interval)} après l'échéance`;
  }
  if (rem.type === "during" && interval) {
    return `Toutes les ${humanDuration(interval)} pendant le travail`;
  }
  if (anchor && at) {
    const deltaMin = Math.round((anchor - at) / 60000);
    if (rem.type === "before" && deltaMin > 0) return `${humanDuration(deltaMin)} avant l'échéance`;
    if (rem.type === "after"  && deltaMin < 0) return `${humanDuration(deltaMin)} après l'échéance`;
    if (Math.abs(deltaMin) < 5) return "À l'échéance";
  }
  // Fallback — just print the ISO date nicely
  if (rem.at) {
    return new Date(rem.at).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  }
  return "Rappel programmé";
}

function RemindersCard({ task, onDeleted }) {
  const [busyId, setBusyId] = useState(null);
  const reminders = task.reminders || [];
  if (reminders.length === 0) return null;

  const anchor = task.deadline || task.execution_date;

  const remove = async (rem) => {
    if (busyId) return;
    setBusyId(rem.id);
    try {
      await api.delete(`/tasks/${task.id}/reminders/${rem.id}`);
      onDeleted?.(rem.id);
      toast.success("Rappel supprimé");
    } catch (err) {
      toast.error("Suppression du rappel impossible", { description: err.response?.data?.detail || "" });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card
      className="p-6 border-[#3A3A3A] shadow-[0_2px_8px_rgba(0,0,0,0.3)]"
      style={{ borderLeft: `4px solid ${YELLOW}` }}
      data-testid="td-reminders-card"
    >
      <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
        <Bell size={14} style={{ color: YELLOW }} /> Rappels programmés ({reminders.length})
      </h3>
      <ul className="space-y-2">
        {reminders.map((rem) => {
          const st = REMINDER_STYLE[rem.type] || REMINDER_STYLE.before;
          return (
            <li
              key={rem.id}
              className="flex items-center gap-3 rounded-md border border-[#3A3A3A] px-3 py-2 bg-white/[0.02]"
              data-testid={`reminder-row-${rem.id}`}
            >
              <span
                className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full flex-shrink-0"
                style={{ background: st.bg, color: st.color, border: `1px solid ${st.color}66` }}
                data-testid={`reminder-badge-${rem.id}`}
              >
                {st.label}
              </span>
              <span className="text-sm flex-1 truncate" data-testid={`reminder-desc-${rem.id}`}>
                {describeReminder(rem, anchor)}
              </span>
              <button
                type="button"
                onClick={() => remove(rem)}
                disabled={busyId === rem.id}
                className="p-1.5 rounded hover:bg-red-500/15 text-muted-foreground hover:text-red-400 disabled:opacity-40"
                title="Supprimer ce rappel"
                data-testid={`reminder-delete-${rem.id}`}
              >
                <Trash2 size={14} />
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
